import VideoPlayer from './VideoPlayer';
import throttle from 'lodash.throttle';
import { getViewPercentage } from '../utils';

/**
 * Utilities to handle chapter videos
 */
export default class Videos {
	static create(chapter) {
		const videos = chapter.content.querySelectorAll('[data-video-id]');

		if (!videos.length) {
			return null;
		}

		return new Videos(chapter);
	}

	constructor(chapter) {
		this.chapter = chapter;

		const platform = this.chapter.getPlatform();
		const selectors = {
			mobile: '[data-video-id-mobile], [data-video-id-both]',
			desktop: '[data-video-id-desktop], [data-video-id-both]',
		};

		this.videoPlayers = Array.from(this.chapter.content.querySelectorAll(selectors[platform])).map(video => {
			return new VideoPlayer(video);
		});

		this.boundHandleScroll = throttle(this.handleScroll.bind(this), 200);
	}

	startObservering() {
		document.addEventListener('scroll', this.boundHandleScroll);
	}

	stopObserving() {
		document.removeEventListener('scroll', this.boundHandleScroll);
	}

	handleScroll() {
		this.videoPlayers.forEach(videoPlayer => {
			const percentage = getViewPercentage(videoPlayer.parent);

			if (percentage > 0) {
				videoPlayer.play();
			} else {
				videoPlayer.pause();
			}
		});
	}

	getUnreadyVideoPlayersPerPart(amount = null) {
		const videoPlayers = [];

		this.chapter.parts.forEach((part, index) => {
			let unreadyVideoPlayers = this.videoPlayers.filter(video => {
				return !video.ready && video.chapterPartIndex == index;
			});

			if (amount) {
				unreadyVideoPlayers = unreadyVideoPlayers.slice(0, amount);
			}

			videoPlayers.push(...unreadyVideoPlayers);
		});

		return videoPlayers;
	}

	getUnreadyVideoPlayers(amount = null) {
		const unreadyVideoPlayers = this.videoPlayers.filter(video => {
			return !video.ready;
		});

		if (amount) {
			return unreadyVideoPlayers.slice(0, amount);
		}

		return unreadyVideoPlayers;
	}

	/**
	 * A player is considered loaded when it's ready to play
	 * @param {*} videoPlayers
	 * @returns {Promise} The loaded players
	 */
	loadVideoPlayers(videoPlayers) {
		return new Promise(resolve => {
			if (!videoPlayers.length) {
				resolve();
				return;
			}

			let counter = 0;

			videoPlayers.forEach(async (videoPlayer, index) => {
				const player = videoPlayer.createPlayer();

				await player.ready();
				await videoPlayer.forceLoad();
				counter++;

				if (counter >= videoPlayers.length) {
					resolve(videoPlayers);
				}
			});
		});
	}

	destroy() {
		this.stopObserving();
		this.videoPlayers.forEach(videoPlayer => videoPlayer.pause());
	}
}
